import * as React from 'react';
import ReactDOM from 'react-dom';
import Hammer from 'hammerjs';

import { cn } from '@/lib/utils';

type DrawerAnchor = 'left' | 'right' | 'top' | 'bottom';
type DrawerCloseReason = 'backdropClick' | 'escapeKeyDown' | 'swipe';

interface DrawerModalProps {
  keepMounted?: boolean;
}

interface DrawerProps {
  anchor?: DrawerAnchor;
  className?: string;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  open: boolean;
  onClose?: (event?: unknown, reason?: DrawerCloseReason) => void;
  onOpen?: (event?: unknown) => void;
  ModalProps?: DrawerModalProps;
  swipeable?: boolean;
  fullScreen?: boolean;
  disableSwipeToOpen?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  children: React.ReactNode;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const EXTERNAL_FOCUS_LAYER_SELECTOR = [
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[aria-modal="true"]',
  '[role="menu"]',
  '[role="listbox"]',
  '[data-radix-popper-content-wrapper]',
].join(',');

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (element) =>
      !element.hidden &&
      element.getAttribute('aria-hidden') !== 'true' &&
      !element.closest('[hidden], [aria-hidden="true"]') &&
      !element.closest('[inert]')
  );

const isInExternalFocusLayer = (
  element: Element | null,
  drawerRoot: HTMLElement
) =>
  Boolean(
    element &&
    !drawerRoot.contains(element) &&
    element.closest(EXTERNAL_FOCUS_LAYER_SELECTOR)
  );

const anchorContentClassName: Record<DrawerAnchor, string> = {
  left: 'left-0 top-0 h-full max-w-[100vw] translate-x-0',
  right: 'right-0 top-0 h-full max-w-[100vw] translate-x-0',
  top: 'left-0 top-0 w-full max-h-[100vh] translate-y-0',
  bottom: 'bottom-0 left-0 w-full max-h-[100vh] translate-y-0',
};

const openStateClassName: Record<DrawerAnchor, string> = {
  left: 'translate-x-0',
  right: 'translate-x-0',
  top: 'translate-y-0',
  bottom: 'translate-y-0',
};

const closedStateClassName: Record<DrawerAnchor, string> = {
  left: '-translate-x-full',
  right: 'translate-x-full',
  top: '-translate-y-full',
  bottom: 'translate-y-full',
};

const swipeDirectionByAnchor: Record<DrawerAnchor, number> = {
  left: Hammer.DIRECTION_LEFT,
  right: Hammer.DIRECTION_RIGHT,
  top: Hammer.DIRECTION_UP,
  bottom: Hammer.DIRECTION_DOWN,
};

const swipeEventByAnchor: Record<DrawerAnchor, string> = {
  left: 'swipeleft',
  right: 'swiperight',
  top: 'swipeup',
  bottom: 'swipedown',
};

const DRAWER_TRANSITION_DURATION_MS = 200;

function Drawer({
  anchor = 'left',
  className,
  contentClassName,
  contentStyle,
  open,
  onClose,
  onOpen,
  ModalProps,
  swipeable = false,
  fullScreen = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  children,
}: DrawerProps) {
  const previousOpenRef = React.useRef(open);
  const previouslyFocusedElementRef = React.useRef<HTMLElement | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const onCloseRef = React.useRef(onClose);
  const keepMounted = ModalProps?.keepMounted;

  onCloseRef.current = onClose;

  const [isMounted, setIsMounted] = React.useState(
    open || Boolean(keepMounted)
  );

  const [isVisible, setIsVisible] = React.useState(false);

  const closedInertProps = open
    ? {}
    : ({ inert: '' } as unknown as React.HTMLAttributes<HTMLDivElement>);

  React.useEffect(() => {
    if (open) {
      setIsMounted(true);
      return;
    }

    setIsVisible(false);

    if (keepMounted) {
      setIsMounted(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsMounted(false);
    }, DRAWER_TRANSITION_DURATION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [keepMounted, open]);

  React.useEffect(() => {
    if (!open || !isMounted) return;

    let nextFrameId: number | undefined;

    const frameId = window.requestAnimationFrame(() => {
      nextFrameId = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);

      if (nextFrameId !== undefined) {
        window.cancelAnimationFrame(nextFrameId);
      }
    };
  }, [isMounted, open]);

  React.useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  React.useEffect(() => {
    const wasOpen = previousOpenRef.current;
    if (open && !wasOpen) {
      onOpen?.();
    }
    previousOpenRef.current = open;
  }, [onOpen, open]);

  React.useEffect(() => {
    if (!open || !isMounted) return;

    previouslyFocusedElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const frameId = window.requestAnimationFrame(() => {
      const content = contentRef.current;
      if (!content) return;

      const autofocusElement =
        content.querySelector<HTMLElement>('[autofocus]');

      const firstFocusableElement = getFocusableElements(content)[0];
      (autofocusElement ?? firstFocusableElement ?? content).focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root) return;

      const eventTarget = event.target instanceof Element ? event.target : null;

      const activeElement =
        document.activeElement instanceof Element
          ? document.activeElement
          : null;

      if (
        isInExternalFocusLayer(eventTarget, root) ||
        isInExternalFocusLayer(activeElement, root)
      ) {
        return;
      }

      if (event.key === 'Escape') {
        onCloseRef.current?.(event, 'escapeKeyDown');
        return;
      }

      if (event.key !== 'Tab') return;

      const content = contentRef.current;
      if (!content) return;

      const focusableElements = getFocusableElements(root);
      const firstFocusableElement = focusableElements[0];

      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      if (!firstFocusableElement || !lastFocusableElement) {
        event.preventDefault();
        content.focus();
        return;
      }

      if (!root.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? lastFocusableElement : firstFocusableElement).focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      } else if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(frameId);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElementRef.current?.focus();
      previouslyFocusedElementRef.current = null;
    };
  }, [isMounted, open]);

  React.useEffect(() => {
    if (!swipeable || !open || !contentRef.current) return;

    const hammer = new Hammer(contentRef.current);
    const swipeEvent = swipeEventByAnchor[anchor];

    hammer.get('swipe').set({ direction: swipeDirectionByAnchor[anchor] });

    hammer.on(swipeEvent, (event) => {
      onClose?.(event, 'swipe');
    });

    return () => {
      hammer.destroy();
    };
  }, [anchor, onClose, open, swipeable]);

  if (!isMounted || typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      ref={rootRef}
      className={cn(
        'ui-drawer-root fixed inset-0 z-[3000]',
        open ? 'pointer-events-auto' : 'pointer-events-none',
        className
      )}
      data-state={isVisible ? 'open' : 'closed'}
      data-anchor={anchor}
      aria-hidden={open ? undefined : true}
      {...closedInertProps}
    >
      <div
        className={cn(
          'ui-drawer-backdrop absolute inset-0 bg-black/50 transition-opacity duration-200',
          isVisible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={(event) => onClose?.(event, 'backdropClick')}
      />
      <div
        ref={contentRef}
        role="dialog"
        aria-modal={open ? true : undefined}
        aria-label={ariaLabel ?? (ariaLabelledby ? undefined : 'Drawer')}
        aria-labelledby={ariaLabelledby}
        tabIndex={-1}
        className={cn(
          'ui-drawer-content absolute overflow-auto bg-background text-foreground shadow-xl transition-transform duration-200 ease-out',
          anchorContentClassName[anchor],
          isVisible ? openStateClassName[anchor] : closedStateClassName[anchor],
          anchor === 'bottom' &&
            'rounded-t-[1.5rem] pb-[env(safe-area-inset-bottom)]',
          // Edge to edge, so the notch and home indicator need both insets.
          fullScreen &&
            'h-[100dvh] max-h-[100dvh] rounded-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
          swipeable && '!w-screen !max-w-none',
          contentClassName
        )}
        data-state={isVisible ? 'open' : 'closed'}
        data-anchor={anchor}
        style={contentStyle}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export { Drawer };
export type { DrawerProps, DrawerAnchor, DrawerCloseReason };
